import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  getBlockOfficerProfile,
  getDistrictOfficerProfile,
  getPrincipleProfile,
  getTeacherProfile,
  logIn,
} from "../Services/UserAuth/UserAuthenticate";
import { TypeLogin } from "../types";
import { isValidDob } from "../utilityFunctions";
import {
  setSession,
  setUserInfo,
  setUserProfile,
  setUserRole,
} from "../Reducers/User/userSlice";
import { AxiosResponse } from "axios";
import { setTeacherSchool } from "../Reducers/User/teacherSlice";
import { PATHS, STATE_OFFICIAL_PATHS } from "../routes";
import { setPrincipleProfile } from "../Reducers/User/principleSlice";
import { clearToken, setToken, setRefreshToken } from "../Services/storeToken";

const useAuth = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [logOutFromOldDevice, setLogOutFromOldDevice] = useState<boolean>(false);
  const [isExclusivelyLogIn, setIsExclusivelyLogIn] = useState<boolean>(false);
  const [userCredentials, setUserCredentials] = useState<{
    srn: string;
    dob: string;
  }>();

  // set a state variable which can be used to disable the save/submit button
  const [disable, setDisabled] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const handleChange = (type: "srn" | "dob", value: string) => {
    setUserCredentials((prev) => ({ ...prev, [type]: value }));
  };
  const toastId = useRef(null); // loading toast

  // we use the help of useRef to test if it's the first render
  const firstRender = useRef<boolean>(true);

  useEffect(() => {
    // skip validation on first render
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setDisabled(formValidation());
  }, [userCredentials]);

  const handleClickYes = (e) => {
    e.preventDefault();
    logInUser(true)
  };

  const formValidation = () => {
    if (userCredentials?.srn?.toString().length === 0) {
      setErrorMessage("Employee code can not be empty!");
      return true;
    } else if (userCredentials?.dob === "") {
      setErrorMessage("DOB can not be empty!");
      return true;
    } else if (
      String(userCredentials?.srn)?.length < 1
    ) {
      setErrorMessage("Employee code has to be of 6 digits!");
      return true;
    } else if (
      +userCredentials?.dob?.split("/")[1] > 12 ||
      +userCredentials?.dob?.split("/")[1] === 0
    ) {
      setErrorMessage("Month in DOB is not valid!");
      return true;
    } else if (
      +userCredentials?.dob?.split("/")[0] > 31 ||
      +userCredentials?.dob?.split("/")[0] === 0
    ) {
      setErrorMessage("Date in DOB is not valid!");
      return true;
    } else {
      setErrorMessage("");
      return false;
    }
  };

  const handleTeacherLoginSuccess = (userId: number) => {
    getTeacherProfile(userId)
      .then(
        async (response: AxiosResponse) => {
          dispatch(setTeacherSchool(response?.data?.data[0])); // stores teacher's school information in redux-store
          // setTimeout(() => { history.push(PATHS.teacherDashboard) }, 500);
          await history.push(PATHS.teacherDashboard);
          setIsLoading(false);
          window.location.reload();
        },
        (error) => {
          console.log("Error caught: ", error?.response);
          toast.error("Can not login! Try again.");
        }
      )
      .catch((error) => console.log("Catched Error ---> ", error));
  };

  const handleDistrictOfficialLogin = (userId: number) => {
    getDistrictOfficerProfile(userId).then((res) => {
      const district = res?.data?.data?.district;
      dispatch(
        setUserProfile({
          district: { id: district?.id, name: district?.name },
          state: { id: district?.state?.id, name: district?.state?.name },
        })
      );
      // history.push(PATHS.districtOfficerDashboard);
      history.push("/stateOfficer-index");
      setIsLoading(false);
      window.location.reload();
    });
  };

  const handleBlockOfficialLogin = (userId: number) => {
    getBlockOfficerProfile(userId).then((res) => {
      const block = res?.data?.data?.block;
      dispatch(
        setUserProfile({
          block: { id: block?.id, name: block?.name },
          district: { id: block?.district?.id, name: block?.district?.name },
          state: {
            id: block?.district?.state?.id,
            name: block?.district?.state?.name,
          },
        })
      );
      // history.push(PATHS.blockOfficerDashboard);
      history.push("/stateOfficer-index");
      setIsLoading(false);
      window.location.reload();
    });
  };

  const handlePrincipalLoginSuccess = (userId: number) => {
    getPrincipleProfile(userId)
      .then(
        async (response: AxiosResponse) => {
          // dispatch(setPrincipleProfile(response?.data?.data?.school)); // stores principal's school information in redux-store
          const school = response?.data?.data?.school;
          const block = school?.block;
          const district = block?.district;
          dispatch(
            setUserProfile({
              district: { id: district?.id, name: district?.name },
              state: { id: district?.state, name: "" },
              block: { id: block?.id, name: block?.name },
              school: {
                id: school?.id,
                name: school?.name,
                medium: { id: school?.medium?.id, type: school?.medium?.type },
                city: school?.city,
                board: { id: school?.board?.id, name: school?.board?.name },
                schoolCode: school?.school_code,
              },
            })
          );
          // await history.push(PATHS.principalDashboard);
          await history.push("/stateOfficer-index");
          setIsLoading(false);
          window.location.reload();
        },
        (error) => {
          console.log("Error caught: ", error?.response);
          toast.error("Can not login! Try again.");
        }
      )
      .catch((error) => console.log("Catched Error ---> ", error));
  };

  const handleLoginError = (error) => {
    toastId.current = false;
    setIsLoading(false);
    if (error?.response?.status === 404) {
      toast.warn("Employee Code or DOB is wrong!", { icon: true });
    } else if (error?.response?.status === 401) {
      toast.warn("You are inactive! Contact administration.");
    } else if (error?.response?.status === 500) {
      toast.warn("We are facing some difficulty! Please wait for some time.");
    } else {
      toast.error("Can not login, try again!");
    }
    console.log("Error occured: ", error?.response);
  };


  const logInUser = async (isExclusively?: boolean) => {

    // try {

    if (userCredentials && userCredentials.srn && userCredentials.dob) {
      setIsLoading(true);
      let loginCredentials: TypeLogin = {
        userName: String(userCredentials?.srn)?.trim() + "@pal.com",
        password:
          String(userCredentials?.srn)?.trim() +
          userCredentials?.dob?.split("/").join(""),
      };
      let retry_count = 0;
      let is_unauthorized = false;
      let res: any = undefined;

      logIn(loginCredentials, isExclusively ? true : false).then((response) => {
        const data = response?.data;
        const isActiveUser = data?.user?.status === "active";
        console.log("login response123455555", data)
        setToken(data?.accessToken);
        setRefreshToken(data?.refreshToken)
        let user = data?.user;
        dispatch(setUserInfo(user));
        // try {
        //   const activeSession = await getActiveSession();
        //   const data = activeSession?.data?.data;
        dispatch(setSession(1));
        // } catch (error) {
        //   console.log("no active session found");
        // }
        let role = Number(data?.user?.role); // manually setting role for navigating to the respective route
        sessionStorage.setItem("role", String(role)); // storing role
        dispatch(setUserRole(role));
        if (isActiveUser) {
          const userId = data?.user?.id;
          // if (role === 3) {
          //   handleTeacherLoginSuccess(userId);
          // } else if (role === 4) {
          //   history.push(PATHS.parentDashboard);
          //   window.location.reload();
          // } else if (role === 5) {
          //   handlePrincipalLoginSuccess(userId);
          // } else if (role === 6) {
          //   handleBlockOfficialLogin(userId);
          // } else if (role === 7) {
          //   handleDistrictOfficialLogin(userId);
          // } 
          if (role === 9) {
            history.push(STATE_OFFICIAL_PATHS.stateOfficerDashboard);
            window.location.reload();
          } else if (role === 7) {
            history.push("/stateOfficer-index");
            window.location.reload();
          } else if (role === 6) {
            history.push("/stateOfficer-index");
            window.location.reload();
          } else if (role === 5) {
            history.push("/stateOfficer-index");
            window.location.reload();
          } else if (role === 1) {
            history.push(PATHS.superAdmin);
            window.location.reload();
          } else {
            toast.warning("You are not authorized to log into this portal!");
            clearToken();
          }
        };
      })
        .catch((err) => {
          const error = err?.response;
          if (error?.status === 412) {
            setLogOutFromOldDevice(true);
          }
          else {
            console.log("Error in authentication: ", err);
            retry_count += 1;
            if (err?.status === 401 || err?.status === 403 || err?.status === 404) { is_unauthorized = true; }
            toast.warning("Something Went Wrong!");
          }
        })
    };
    setIsLoading(false);

  };

  return {
    logInUser,
    isLoading,
    handleChange,
    errorMessage,
    userCredentials,
    disable,
    logOutFromOldDevice,
    setLogOutFromOldDevice,
    handleClickYes
  };
};

export default useAuth;
